/**
 * Recording state reconciliation
 *
 * The REC badge is driven by a list of channel ids built from RECORDING_START / RECORDING_END
 * events. A delta list like that is only correct while every event arrives, and RECORDING_END does
 * not always arrive:
 *
 *  - The PBX publishes RECORDING_END only after the recording has been uploaded and stored. When it
 *    discards the recording instead ("no audio was captured") or the stop path throws, no event is
 *    published at all, even though the call is over.
 *  - A client that is asleep, locked or otherwise disconnected simply misses the event.
 *
 * Either way the channel id stays in the list forever and the badge keeps claiming we are recording.
 * These helpers keep the list sane, and tell the store when it needs to re-check the authoritative
 * state at /pbx/recording/status.
 */

import type { PbxRecordingStatusItem } from '@/TcxApi/PbxRecording'

/**
 * Add a channel to the recorded set, ignoring one we already track
 * @param ids Channels currently being recorded
 * @param channel Channel that started recording
 * @returns The new list of recorded channels
 */
export function addRecordingChannel(ids: readonly string[], channel: string): string[] {
	if (ids.includes(channel)) { return [...ids] }
	return [...ids, channel]
}

/**
 * Remove a channel from the recorded set - every copy of it, so an earlier duplicate cannot survive
 * @param ids Channels currently being recorded
 * @param channel Channel that stopped recording
 * @returns The new list of recorded channels
 */
export function removeRecordingChannel(ids: readonly string[], channel: string): string[] {
	return ids.filter(id => id !== channel)
}

/**
 * Build the recorded set from the authoritative status the API collected from the PBX servers
 * @param status Status of each of my ongoing calls
 * @returns The channels the PBX confirms it is recording
 */
export function recordingIdsFromStatus(status: readonly PbxRecordingStatusItem[]): string[] {
	const ids: string[] = []

	for (const item of status) {
		if (item.recording && !ids.includes(item.channel)) { ids.push(item.channel) }
	}
	return ids
}

/**
 * Find recorded channels that have no ongoing call behind them any more. A recording cannot outlive
 * its call, so these are leftovers from a RECORDING_END we never received - the signal to go and
 * verify the real state rather than a reason to drop them straight away (the ongoing calls we see
 * are filtered down to the device in use, so a call can be missing here and still be live).
 * @param ids Channels currently being recorded
 * @param activeChannels Channels of the calls that are ongoing
 * @returns The recorded channels with no matching ongoing call
 */
export function orphanedRecordingIds(ids: readonly string[], activeChannels: readonly string[]): string[] {
	return ids.filter(id => !activeChannels.includes(id))
}
